import { useAuth } from "@/contexts/AuthContext";
import { withReturnTo } from "@/lib/returnTo";

/**
 * Smart CTA destination helper.
 *
 * Returns the right route depending on the user's auth state and the intent of the CTA,
 * so signed-in users never land on /signup or /login by mistake.
 *
 * Usage:
 *   const cta = useSmartCTA();
 *   <Link to={cta.postJob()} ...>Post a Job</Link>
 *   <Link to={cta.becomeFixyBee()} ...>Become a FixyBee</Link>
 *   <Link to={cta.signup("client")} ...>Sign up free</Link>
 */
export function useSmartCTA() {
  const { user, loading } = useAuth();
  const role = user?.profile?.role;
  const isAdmin = user?.profile?.is_admin || role === "admin";
  const isProvider = role === "provider";
  const isClient = role === "client";
  // Onboarding is finished exactly when the account has a role. Since
  // 2026-10-28 `/auth/signup` writes it at creation, so only an account from
  // before then can be without one; ProtectedRoute redirects on the same fact.
  const needsOnboarding = !!user && !role;

  const dashboardPath = isAdmin
    ? "/admin"
    : isProvider
    ? "/provider/dashboard"
    : isClient
    ? "/client/dashboard"
    : null;

  // Lifted out of the object literal below because `quoteJob` needs it: the
  // anonymous destination has exactly one definition, and both CTAs use it.
  const becomeFixyBee = (opts = {}) => {
    if (isProvider) return "/provider/dashboard";
    // Everyone else (anon, client, admin) → conversion landing, whose own
    // buttons go to `/signup/provider`. `returnTo` rides along when the CTA
    // that got here meant something more specific than "become a FixyBee" —
    // the landing forwards it to those buttons.
    return withReturnTo("/become-a-fixybee", opts.returnTo);
  };

  return {
    // "I want to post a job"
    //
    // `opts.forProvider` is a FixyBee's public slug, passed by the CTA on that
    // FixyBee's own page. It rides to the form as `?for=`, and the form posts
    // the job *for* them: one notification to one person, no FixyMatch blast
    // ([FIX] 2026-09-07 — until then the CTA carried nothing at all and the
    // request reached the whole category).
    //
    // Carried for an anonymous visitor too, since `lib/returnTo.js`: they go to
    // signup and the slug rides in `returnTo`, so they arrive at the form with
    // the FixyBee still named instead of posting an ordinary job.
    postJob: (opts = {}) => {
      const forProvider = opts.forProvider
        ? `?for=${encodeURIComponent(opts.forProvider)}`
        : "";
      // `/jobs/new`, not either namespaced form: an anonymous visitor has no
      // role yet, and onboarding is what decides which of the two addresses
      // exists for them. The neutral route resolves it after the fact, so the
      // `?for=` a directed request depends on survives the whole trip.
      // (`intent=post-job` used to ride here and was read by nothing.)
      if (!user) {
        return withReturnTo("/signup/client", `/jobs/new${forProvider}`);
      }
      if (isClient) return `/client/jobs/new${forProvider}`;
      // DY2.6: a FixyBee posting a job is no longer a category error — it is
      // the same errand from the other side of the marketplace, and the form
      // lives in their own namespace. Admins keep going to their dashboard;
      // there is no admin job form and never was.
      if (isProvider) return `/provider/requests/new${forProvider}`;
      return dashboardPath || "/";
    },

    // "I want to become a FixyBee (provider)"
    becomeFixyBee,

    // "I want to quote THIS job" — the CTA on a public job page.
    //
    // Returns a destination and the state that produced it, because a caller
    // that only got a URL would still have to re-derive which of the five
    // sentences to print, and that re-derivation is the bug this replaces:
    // every public CTA already routed by auth and then wrote anonymous copy
    // over the top of it.
    //
    // `job` is the payload from `/jobs/public/:id` — `is_mine` and
    // `already_quoted` are the reader's own facts, and the server refuses both
    // cases anyway (403 `cannot_quote_own_job`, 400 for a second quote). The
    // browser is a convenience here and never the rule.
    quoteJob: (job) => {
      const jobId = job?.job_id;
      // `/auth/me` is still in flight. Until it lands, `user` is null and every
      // branch below would read as "anonymous" — which is the bug this function
      // exists to kill, just briefly enough to blame on a blink. The caller
      // renders a placeholder for this state and commits to no sentence.
      if (loading) return { state: "pending", to: null };
      if (!user) {
        // They want to quote THIS job. Say so, so that after signing up they
        // arrive at the quote form for it rather than at a dashboard. Guarded
        // on `jobId`: a card rendered before its payload lands would otherwise
        // promise `?job=undefined`, and the generic landing is the honest
        // answer while there is no job to name.
        return {
          state: "anon",
          to: becomeFixyBee(
            jobId ? { returnTo: `/provider/quotes/new?job=${jobId}` } : {}
          ),
        };
      }
      if (needsOnboarding) return { state: "onboarding", to: "/signup/client" };
      if (job?.is_mine) {
        return {
          state: "own",
          to: isProvider ? "/provider/requests" : "/client/jobs",
        };
      }
      if (isProvider && job?.already_quoted) {
        return { state: "quoted", to: "/provider/quotes" };
      }
      if (isProvider) {
        return { state: "quote", to: `/provider/quotes/new?job=${jobId}` };
      }
      // A client, or an admin. Neither quotes; neither is being sold a signup
      // they already completed. They get the neutral card and their own place
      // to go — a client posting a job of their own is the honest offer here.
      if (isClient) return { state: "client", to: "/client/jobs/new" };
      return { state: "neutral", to: dashboardPath || "/" };
    },

    // The hero of the open-jobs showcase — the same question as `quoteJob`,
    // asked about the board rather than about one row.
    browseOpenJobs: () => {
      if (loading) return { state: "pending", to: null };
      if (!user) {
        return {
          state: "anon",
          to: becomeFixyBee({ returnTo: "/provider/browse" }),
        };
      }
      if (needsOnboarding) return { state: "onboarding", to: "/signup/client" };
      // An onboarded FixyBee wants the real board, not the marketing landing:
      // /provider/browse is the same demand with the quote button attached.
      if (isProvider) return { state: "provider", to: "/provider/browse" };
      if (isClient) return { state: "client", to: "/client/jobs/new" };
      return { state: "neutral", to: dashboardPath || "/" };
    },

    // Sign-up: one page per side of the marketplace, and the destination
    signup: (role, opts = {}) => {
      if (user) return dashboardPath || "/";
      return withReturnTo(role === "provider" ? "/signup/provider" : "/signup/client",
                          opts.returnTo);
    },

    // "Find FixyBees" (directory) — always public
    browseProviders: () => "/providers",

    // Pricing CTAs by plan
    planCheckout: (tier) => {
      if (!user) {
        return withReturnTo("/signup/provider", "/provider/billing");
      }
      if (isProvider) return "/provider/billing";
      return dashboardPath || "/";
    },

    user,
    isLoggedIn: !!user,
    isClient,
    isProvider,
    isAdmin,
    needsOnboarding,
    authLoading: loading,
    dashboardPath,
  };
}

export default useSmartCTA;
